import { EventType } from "@/analytics/analytics-events";
import {
  SelectCaptureProperties,
  SelectCaptureSources,
} from "@/analytics/generic-events-properties";
import NotchBracketSvg from "@/components/ui/icons/notch-bracket.svg";
import { useImgPreviewSignedUrl } from "@/hooks/use-img-preview-signed-url";
import { IElementImg } from "@/hooks/use-placeholders";
import { useAppSelector } from "@/store/store-hooks";
import {
  blue,
  FaroIconButton,
  FaroIconButtonSizes,
  HorizontalScrollList,
  MarkupPolygonIcon,
  grey,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { GUID, isLoading } from "@faro-lotv/foundation";
import {
  IElementImageCommand,
  IElementImg2d,
  isIElementImageCommand,
  isIElementImg360,
} from "@faro-lotv/ielement-types";
import {
  selectIElementWorldPosition,
  useValidUrl,
} from "@faro-lotv/project-source";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, CardMedia, Skeleton, Stack } from "@mui/material";
import { isEqual } from "es-toolkit";
import {
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";
import {
  isElementPhotosGroup,
  isPanoWithPhotoObjects,
  PanoWithPhotoObjects,
  selectPhotoGalleryImgElements,
  selectWalkLocation,
  TimestampElement,
} from "./walk-overlay-selectors";
import { WalkSceneActiveElement } from "./walk-types";

const PHOTO_HEIGHT_IN_GALLERY = 58;
const PHOTO_HEIGHT_IN_CAROUSEL = 66;

/**
 * Callback function type for handling photo selection events.
 *
 * @param id - The unique identifier (GUID) of the selected photo.
 * @param event - Optional React mouse event that triggered the selection.
 */
type SelectPhotoCallback = (id: GUID, event?: React.MouseEvent) => void;

type PhotoPreviewListProps = {
  /** Photo array of a given 2D photo group */
  photos: IElementImg2d[];

  /** Index of the selected photo*/
  selected: number;

  /** Call back  when a photo is selected */
  onPhotoSelected: SelectPhotoCallback;
};

/**
 * @returns horizontal scroll photo previews
 */
export function PhotoPreviewList({
  photos,
  selected,
  onPhotoSelected,
}: PhotoPreviewListProps): JSX.Element {
  const imgPreviewCards = useMemo(
    () =>
      photos.map((img, index) => (
        <PhotoCard
          key={index}
          imgElement={img}
          selected={index === selected}
          imageHeight={PHOTO_HEIGHT_IN_CAROUSEL}
          onPhotoSelected={(id) => {
            Analytics.track<SelectCaptureProperties>(EventType.selectCapture, {
              via: SelectCaptureSources.carousel,
              type: "2D",
            });
            onPhotoSelected(id);
          }}
        />
      )),
    [onPhotoSelected, photos, selected],
  );

  const onPrevious = useCallback(() => {
    const index = selected > 0 ? selected - 1 : photos.length - 1;
    onPhotoSelected(photos[index].id);
  }, [onPhotoSelected, photos, selected]);

  const onNext = useCallback(() => {
    const index = selected < photos.length - 1 ? selected + 1 : 0;
    onPhotoSelected(photos[index].id);
  }, [onPhotoSelected, photos, selected]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") {
        return onPrevious();
      }

      if (event.key === "ArrowRight") {
        return onNext();
      }
    },
    [onPrevious, onNext],
  );

  useEffect(() => {
    window.addEventListener("keydown", onKeyDown);

    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onKeyDown]);

  return (
    <Stack sx={{ userSelect: "none" }}>
      <HorizontalScrollList
        isPageScroll
        wrapperSx={{
          width: "fit-content",
          maxWidth: 380,
          backgroundColor: grey[1000],
        }}
        sx={{ p: 1, pt: 1.5 }}
        scrollButtonProps={{
          size: FaroIconButtonSizes.xs,
          color: grey[100],
          hoverColor: blue[500],
          sx: {
            boxShadow: `0px 0px 4px ${grey[100]}CC`,
            background: grey[950],
            "&:hover": {
              background: grey[800],
            },
            "&:active": {
              background: grey[700],
            },
          },
        }}
      >
        {imgPreviewCards}
      </HorizontalScrollList>

      <FaroIconButton
        size="l"
        color={grey[1300]}
        onClick={onPrevious}
        sx={{ position: "absolute", top: "50%", left: "0" }}
      >
        <ChevronLeftIcon />
      </FaroIconButton>
      <FaroIconButton
        size="l"
        color={grey[1300]}
        onClick={onNext}
        sx={{ position: "absolute", top: "50%", right: "5%" }}
      >
        <ChevronRightIcon />
      </FaroIconButton>
    </Stack>
  );
}

export type PhotoCardProps = {
  /** 2D or 360 photo or image command IElement */
  imgElement: IElementImg | IElementImageCommand;

  /** True if the photo is selected for view in the full screen*/
  selected: boolean;

  /** The image height */
  imageHeight: number;

  /** Call back  when the photo is selected */
  onPhotoSelected: SelectPhotoCallback;
};

/**
 * @returns Photo card component
 */
export function PhotoCard({
  imgElement,
  selected,
  imageHeight,
  onPhotoSelected,
}: PhotoCardProps): JSX.Element {
  const imgUrl = useImgPreviewSignedUrl(imgElement);
  const cardRef = useRef<HTMLDivElement>(null);

  const [isPreviewLoading, setIsPreviewImageLoading] = useState(true);

  const isImageLoading = isLoading(imgUrl) || (isPreviewLoading && !!imgUrl);
  const cardImage = isLoading(imgUrl) ? undefined : (imgUrl ?? undefined);
  const signedCardImage = useValidUrl(cardImage);

  const imageWidth = useMemo(() => {
    if (isIElementImg360(imgElement)) {
      // for pano, force the width 2 times of the height
      return 2 * imageHeight;
    }

    // for 2D image, keep the aspect ratio
    return imgElement.pixelWidth && imgElement.pixelHeight
      ? (imgElement.pixelWidth / imgElement.pixelHeight) * imageHeight
      : imageHeight;
  }, [imageHeight, imgElement]);

  // When selected, there will be border around image added.
  // Need reduce the height so that selected and unselected will show as the same size.
  const cardHeight = selected ? imageHeight - 4 : imageHeight;
  const cardWidth = selected ? imageWidth - 4 : imageWidth;

  // Scrolls the card into view if selected (and try to keep it in the middle region)
  useEffect(() => {
    if (!cardRef.current || !selected) return;
    cardRef.current.parentElement?.scrollTo(
      cardRef.current.offsetLeft -
        (cardRef.current.parentElement.clientWidth -
          cardRef.current.clientWidth) /
          2,
      0,
    );
  }, [selected, isImageLoading]);

  return (
    <Box
      ref={cardRef}
      component="div"
      key={imgElement.id}
      sx={{
        position: "relative",
        border: selected ? `2px solid ${blue[500]}` : "none",
        cursor: "pointer",
        borderRadius: "4px",
      }}
    >
      {isImageLoading && (
        <Box component="div" sx={{ height: cardHeight, width: cardWidth }}>
          <Skeleton
            variant="rectangular"
            height="100%"
            width="100%"
            sx={{
              background: grey[800],
              objectFit: "fill",
              borderRadius: selected ? "2px" : "4px",
            }}
          />
        </Box>
      )}

      <CardMedia
        component="img"
        onLoad={() => setIsPreviewImageLoading(false)}
        onError={() => setIsPreviewImageLoading(false)}
        image={signedCardImage}
        sx={{
          height: isImageLoading ? 0 : cardHeight,
          width: cardWidth,
          borderRadius: selected ? "2px" : "4px",
        }}
        onClick={(ev) => {
          ev.stopPropagation();
          onPhotoSelected(imgElement.id, ev);
        }}
      />
      {isIElementImageCommand(imgElement) && (
        <MarkupPolygonIcon
          sx={{
            width: "15px",
            height: "15px",
            position: "absolute",
            top: "5%",
            right: "5%",
            color: grey[800],
          }}
        />
      )}
    </Box>
  );
}

export type PhotoGallery = {
  /** The name of room section location */
  location: string;

  /**
   * The photo gallery of the given location.
   * The key is the id of TimestampElement
   * The content for each key is the photo preview list of the given TimestampElement
   */
  gallery: Record<GUID, ReactNode>;
};

/**
 * @param elements the array of time stamp elements
 * @param activeWalkElement the main element the user is navigating in walk mode
 * @param onPhotoSelected the callback function when a photo is selected
 * @param onPhotoObjectChanged the callback function when a 2D photo object (image command) is selected
 * @returns the photo gallery
 */
export function usePhotoGallery(
  elements: TimestampElement[],
  activeWalkElement: WalkSceneActiveElement,
  onPhotoSelected: SelectPhotoCallback,
  onPhotoObjectChanged: SelectPhotoCallback,
): PhotoGallery | undefined {
  const location = useAppSelector(selectWalkLocation(activeWalkElement));

  const walkPosition = useAppSelector(
    selectIElementWorldPosition(activeWalkElement.id),
    isEqual,
  );

  const imgMap = useAppSelector(
    selectPhotoGalleryImgElements(walkPosition, elements),
  );

  return useMemo(() => {
    const gallery: Record<GUID, JSX.Element> = {};
    for (const key in imgMap) {
      if (isElementPhotosGroup(imgMap[key])) {
        gallery[key] = (
          <GalleryEntry>
            {imgMap[key].photos.map((img, index) => (
              <PhotoCard
                key={index}
                imgElement={img}
                selected={img.id === activeWalkElement.id}
                imageHeight={PHOTO_HEIGHT_IN_GALLERY}
                onPhotoSelected={(id) => {
                  Analytics.track<SelectCaptureProperties>(
                    EventType.selectCapture,
                    {
                      via: SelectCaptureSources.gallery,
                      type: "2D",
                    },
                  );
                  onPhotoSelected(id);
                }}
              />
            ))}
          </GalleryEntry>
        );
      } else if (isPanoWithPhotoObjects(imgMap[key])) {
        gallery[key] = (
          <PhotoObjectsGalleryEntry
            element={imgMap[key]}
            activeWalkElement={activeWalkElement}
            onPhotoSelected={onPhotoSelected}
            onPhotoObjectChanged={onPhotoObjectChanged}
          />
        );
      } else if (isIElementImg360(imgMap[key])) {
        const photoCard = (
          <PhotoCard
            imgElement={imgMap[key]}
            selected={imgMap[key].id === activeWalkElement.id}
            imageHeight={PHOTO_HEIGHT_IN_GALLERY}
            onPhotoSelected={(id) => {
              Analytics.track<SelectCaptureProperties>(
                EventType.selectCapture,
                {
                  via: SelectCaptureSources.gallery,
                  type: "360",
                },
              );
              onPhotoSelected(id);
            }}
          />
        );
        gallery[key] = <GalleryEntry>{photoCard}</GalleryEntry>;
      }
    }

    if (Object.keys(gallery).length > 0) {
      return {
        location,
        gallery,
      };
    }
  }, [
    activeWalkElement,
    imgMap,
    location,
    onPhotoObjectChanged,
    onPhotoSelected,
  ]);
}

function GalleryEntry({ children }: PropsWithChildren): JSX.Element {
  return (
    <HorizontalScrollList
      shouldShowButtons={false}
      wrapperSx={{
        width: "fit-content",
        maxWidth: "40vw",
      }}
      sx={{ p: 0, pt: 1 }}
      scrollBarSx={{
        backgroundColor: grey[900],
        thumbColor: grey[950],
        thumbHoverColor: grey[900],
      }}
    >
      {children}
    </HorizontalScrollList>
  );
}

type PhotoObjectsGalleryEntryProps = {
  /** The entry element of PanoWithPhotoObjects */
  element: PanoWithPhotoObjects;
  /** The active walk element */
  activeWalkElement: WalkSceneActiveElement;
  /** Call back  when a photo is selected */
  onPhotoSelected: SelectPhotoCallback;
  /** Call back  when an image command (2D photo object) is clicked */
  onPhotoObjectChanged: SelectPhotoCallback;
};

const BRACKET = (
  <CardMedia
    component="img"
    height={PHOTO_HEIGHT_IN_GALLERY}
    width="100%"
    image={NotchBracketSvg}
    sx={{
      objectFit: "fill",
    }}
  />
);

function PhotoObjectsGalleryEntry({
  element,
  activeWalkElement,
  onPhotoSelected,
  onPhotoObjectChanged,
}: PhotoObjectsGalleryEntryProps): JSX.Element {
  const photoCard = (
    <PhotoCard
      imgElement={element.pano}
      selected={element.pano.id === activeWalkElement.id}
      imageHeight={PHOTO_HEIGHT_IN_GALLERY}
      onPhotoSelected={(id) => {
        Analytics.track<SelectCaptureProperties>(EventType.selectCapture, {
          via: SelectCaptureSources.gallery,
          type: "360",
        });
        onPhotoSelected(id);
      }}
    />
  );

  const imgCommandCards = useMemo(
    () =>
      element.imageCommands.map((img, index) => (
        <PhotoCard
          key={index}
          imgElement={img}
          selected={false}
          imageHeight={PHOTO_HEIGHT_IN_GALLERY}
          onPhotoSelected={(id) => {
            Analytics.track<SelectCaptureProperties>(EventType.selectCapture, {
              via: SelectCaptureSources.gallery,
              type: "2D object",
            });
            onPhotoSelected(element.pano.id);
            onPhotoObjectChanged(id);
          }}
        />
      )),
    [
      element.imageCommands,
      element.pano.id,
      onPhotoObjectChanged,
      onPhotoSelected,
    ],
  );

  return (
    <GalleryEntry>{[photoCard, BRACKET, ...imgCommandCards]}</GalleryEntry>
  );
}
